import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  cn,
  Input,
  Label,
  Modal,
  Textarea,
  toast,
} from "@devhop/ui";
import type { LucideIcon } from "lucide-react";
import {
  Banknote,
  Building2,
  CheckCircle2,
  CreditCard,
  Loader2,
  MoreHorizontal,
  PlusIcon,
  ReceiptText,
  ScrollText,
  Upload,
} from "lucide-react";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import {
  inferPaymentKindForInvoice,
  paymentKindLabel,
} from "@/modules/quotation-invoice/domain/infer-payment-kind";
import type {
  DocumentType,
  QuotationInvoiceDocumentPaymentDTO,
  QuotationInvoicePaymentKind,
} from "@/modules/quotation-invoice/domain/types";
import { config } from "@/shared/lib/config";
import { formatDateLocal, toDateOnlyString } from "@/shared/lib/date-time";
import { useConvertMyInvoiceToReceipt, useRecordMyDocumentPayment } from "../api/queries";
import { useNavigate } from "@tanstack/react-router";
import { computeNetReceived } from "../lib/compute-net-received";
import { formatDocumentCurrency } from "../lib/format-document-currency";
import { uploadPaymentSlipFile } from "../lib/upload-payment-slip";

function fileUrl(fileKey: string): string {
  return `${config.apiUrl}/files/${fileKey}`;
}

const KIND_OPTIONS: { value: QuotationInvoicePaymentKind; label: string }[] = [
  { value: "deposit", label: "ມັດຈຳ" },
  { value: "partial", label: "ຊຳລະບາງສ່ວນ" },
  { value: "final", label: "ຊຳລະຄົບ" },
  { value: "refund", label: "ຄືນເງິນ" },
];

const PAYMENT_METHOD_OPTIONS: {
  value: string;
  label: string;
  Icon: LucideIcon;
}[] = [
  { value: "bank_transfer", label: "ໂອນເງິນ", Icon: Building2 },
  { value: "cash", label: "ເງິນສົດ", Icon: Banknote },
  { value: "card", label: "ບັດ / QR", Icon: CreditCard },
  { value: "cheque", label: "ເຊັກ", Icon: ScrollText },
  { value: "other", label: "ອື່ນ", Icon: MoreHorizontal },
];

function paymentMethodLabel(value: string | null | undefined): string {
  if (!value?.trim()) return "";
  const hit = PAYMENT_METHOD_OPTIONS.find((o) => o.value === value);
  return hit?.label ?? value;
}

type DocumentPaymentsSectionProps = {
  documentId: string;
  documentType?: DocumentType;
  currency: string;
  documentTotal: number;
  status: string;
  payments: QuotationInvoiceDocumentPaymentDTO[];
};

type PaymentSlipRow =
  | { source: "server"; fileKey: string; fileName: string }
  | { source: "local"; id: string; file: File };

const AMOUNT_EPS = 0.005;

function parsePaymentAmount(raw: string): number | null {
  const s = raw.trim().replace(/,/g, "");
  if (s === "") return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

function projectedNetAfterSave(
  payments: QuotationInvoiceDocumentPaymentDTO[],
  kind: QuotationInvoicePaymentKind,
  amount: number,
): number {
  const base = computeNetReceived(
    payments.map((p) => ({ kind: p.kind, amount: p.amount })),
  );
  const delta = kind === "refund" ? -amount : amount;
  return Math.round((base + delta) * 100) / 100;
}

export function DocumentPaymentsSection({
  documentId,
  documentType = "invoice",
  currency,
  documentTotal,
  status,
  payments,
}: DocumentPaymentsSectionProps) {
  const createPayment = useRecordMyDocumentPayment(documentId);
  const convertToReceipt = useConvertMyInvoiceToReceipt(documentId);
  const nav = useNavigate();

  const [addOpen, setAddOpen] = useState(false);

  const [isRefund, setIsRefund] = useState(false);
  const [amount, setAmount] = useState("");
  const [paidAt, setPaidAt] = useState("");
  const [method, setMethod] = useState("");
  const [notes, setNotes] = useState("");
  const [slipRows, setSlipRows] = useState<PaymentSlipRow[]>([]);
  const [uploadingSlips, setUploadingSlips] = useState(false);

  const net = useMemo(
    () =>
      computeNetReceived(
        payments.map((p) => ({ kind: p.kind, amount: p.amount })),
      ),
    [payments],
  );

  const remaining = useMemo(
    () => Math.round((documentTotal - net) * 100) / 100,
    [documentTotal, net],
  );

  /** ຍອດເຫຼືອຕາມ Invoice — ໃຊ້ເປັນຄ່າເລີ່ມຕົ້ນຊ່ອງຈຳນວນເງິນ */
  const defaultAmountInput = useMemo(() => {
    if (remaining <= AMOUNT_EPS) return "";
    return remaining.toFixed(2);
  }, [remaining]);

  const resetForm = useCallback(() => {
    setIsRefund(false);
    setAmount(defaultAmountInput);
    setPaidAt(toDateOnlyString(new Date()));
    setMethod("bank_transfer");
    setNotes("");
    setSlipRows([]);
  }, [defaultAmountInput]);

  useEffect(() => {
    if (addOpen) {
      resetForm();
    }
  }, [addOpen, resetForm]);

  const fmt = useCallback(
    (n: number) => formatDocumentCurrency(n, currency),
    [currency],
  );

  const maxReceiveThisEntry =
    documentTotal > 0 ? Math.round((documentTotal - net) * 100) / 100 : null;

  const amountLimitHint = useMemo(() => {
    if (isRefund) return `ສູງສຸດຄືນໄດ້: ${fmt(net)}`;
    if (documentTotal > 0 && maxReceiveThisEntry != null) {
      return `ສູງສຸດຕາມຍອດເຫຼືອ: ${fmt(maxReceiveThisEntry)}`;
    }
    return null;
  }, [isRefund, fmt, net, documentTotal, maxReceiveThisEntry]);

  const previewKind = useMemo(() => {
    if (isRefund) return null;
    const n = parsePaymentAmount(amount);
    if (n == null) return null;
    return inferPaymentKindForInvoice({
      netBefore: net,
      amount: n,
      documentTotal,
      isRefund: false,
    });
  }, [amount, net, documentTotal, isRefund]);

  const canRecord =
    status === "issued" || status === "partially_paid" || status === "paid";

  /** ຍອດເຫຼືອຊຳລະຄົບແລ້ວ — ບໍ່ໃຫ້ບັນທຶກການຊຳລະເພີ່ມ */
  const canAddPayment =
    canRecord && remaining > AMOUNT_EPS;

  useEffect(() => {
    if (addOpen && !canAddPayment) {
      setAddOpen(false);
    }
  }, [addOpen, canAddPayment]);

  const buildAttachmentsPayload = useCallback(async () => {
    const out: { fileKey: string; fileName: string }[] = [];
    for (const row of slipRows) {
      if (row.source === "server") {
        out.push({ fileKey: row.fileKey, fileName: row.fileName });
      } else {
        const up = await uploadPaymentSlipFile(row.file, documentId);
        out.push(up);
      }
    }
    return out;
  }, [slipRows, documentId]);

  const submitCreate = async () => {
    const n = parsePaymentAmount(amount);
    if (n == null) {
      toast.error("ປ້ອນຈຳນວນເງິນທີ່ຖືກຕ້ອງ (ຕົວເລກບວກ, ສາມາດໃຊ້ comma ຄັ້ນໜ່ຽວ)");
      return;
    }
    if (isRefund) {
      if (n > net + AMOUNT_EPS) {
        toast.error(`ຄືນເງິນໄດ້ບໍ່ເກີນ ${fmt(net)}`);
        return;
      }
    }
    const kind = inferPaymentKindForInvoice({
      netBefore: net,
      amount: n,
      documentTotal,
      isRefund,
    });
    if (documentTotal > 0) {
      const projected = projectedNetAfterSave(payments, kind, n);
      if (projected > documentTotal + AMOUNT_EPS) {
        toast.error(
          !isRefund && maxReceiveThisEntry != null
            ? `ຍອດບໍ່ເກີນ ${fmt(maxReceiveThisEntry)} ໄດ້ (ຍອດເອກະສານ ${fmt(documentTotal)}, ເຫຼືອຕາມລາຍການ ${fmt(remaining)})`
            : `ຍອດຮັບສະສົມຄົບຫຼັງບັນທຶກຕ້ອງບໍ່ເກີນຍອດເອກະສານ (${fmt(documentTotal)})`,
        );
        return;
      }
    }
    setUploadingSlips(true);
    let attachments: { fileKey: string; fileName: string }[];
    try {
      attachments = await buildAttachmentsPayload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ອັບໂຫຼດສະລິບລົ້ມເຫຼວ");
      setUploadingSlips(false);
      return;
    }
    try {
      await createPayment.mutateAsync({
        amount: n,
        isRefund,
        paidAt: paidAt.trim() || null,
        method: method.trim() || null,
        notes: notes.trim() || null,
        attachments,
      });
      setAddOpen(false);
      resetForm();
    } finally {
      setUploadingSlips(false);
    }
  };

  const addSlipFiles = (files: FileList | null) => {
    if (!files?.length) return;
    setSlipRows((prev) => [
      ...prev,
      ...Array.from(files).map((file) => ({
        source: "local" as const,
        id: nanoid(),
        file,
      })),
    ]);
  };

  const removeSlipRow = (idx: number) => {
    setSlipRows((prev) => prev.filter((_, i) => i !== idx));
  };

  if (documentType !== "invoice") {
    return null;
  }

  if (status === "draft" || status === "cancelled") {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle className="font-semibold text-base">
            ການຊຳລະ / ມັດຈຳ
          </CardTitle>
          {canAddPayment && (
            <Button
              type="button"
              size="sm"
              className="gap-1"
              onClick={() => {
                resetForm();
                setAddOpen(true);
              }}
            >
              <PlusIcon className="h-4 w-4" />
              ບັນທຶກການຊຳລະ
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground text-xs">
            ປະເພດມັດຈຳ / ບາງສ່ວນ / ປິດງວດ — ລະບົບກຳນົດອັດຕະໂນມັດຕາມຍອດ;
            ໃຊ້ຊ່ອງຄືນເງິນເມື່ອຕ້ອງຄືນ
          </p>
          <div className="grid gap-2 text-sm sm:grid-cols-3">
            <div>
              <span className="text-muted-foreground">ຍອດເອກະສານ</span>
              <p className="font-medium">{fmt(documentTotal)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">ຮັບແລ້ວ (ສຸດທິ)</span>
              <p className="font-medium">{fmt(net)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">ເຫຼືອ</span>
              <p className="font-medium">{fmt(remaining)}</p>
            </div>
          </div>

          {payments.length === 0 ? (
            <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີການບັນທຶກຊຳລະ</p>
          ) : (
            <ul className="space-y-3">
              {payments.map((p) => (
                <li
                  key={p.id}
                  className="flex flex-wrap items-start justify-between gap-2 border-b pb-3 last:border-0"
                >
                  <div className="min-w-0 space-y-1">
                    <p className="font-medium">
                      {KIND_OPTIONS.find((o) => o.value === p.kind)?.label ??
                        p.kind}{" "}
                      <span className="text-primary">{fmt(p.amount)}</span>
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {p.paidAt ? formatDateLocal(p.paidAt) : "—"} ·{" "}
                      {p.recordedByName ?? p.recordedBy}
                      {p.method ? ` · ${paymentMethodLabel(p.method)}` : ""}
                    </p>
                    {p.notes && (
                      <p className="text-muted-foreground text-sm">{p.notes}</p>
                    )}
                    {p.attachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {p.attachments.map((a) => (
                          <a
                            key={a.id}
                            href={fileUrl(a.fileKey)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={fileUrl(a.fileKey)}
                              alt={a.fileName}
                              className="h-16 w-16 rounded border object-cover"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {p.receiptId ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        onClick={() =>
                          void nav({
                            to: "/my-documents/$id",
                            params: { id: p.receiptId! },
                          })
                        }
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {p.receiptNumber ?? "ໃບຮັບເງິນ"}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1 text-xs"
                        disabled={convertToReceipt.isPending}
                        onClick={() =>
                          void convertToReceipt.mutateAsync({
                            id: documentId,
                            paymentId: p.id,
                          })
                        }
                      >
                        {convertToReceipt.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <ReceiptText className="h-3.5 w-3.5" />
                        )}
                        ອອກໃບຮັບ
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Modal
        open={addOpen}
        onOpenChange={setAddOpen}
        title="ບັນທຶກການຊຳລະ"
        size="lg"
      >
        <PaymentFormFields
          isRefund={isRefund}
          setIsRefund={setIsRefund}
          previewKind={previewKind}
          amount={amount}
          setAmount={setAmount}
          amountLimitHint={amountLimitHint}
          paidAt={paidAt}
          setPaidAt={setPaidAt}
          method={method}
          setMethod={setMethod}
          notes={notes}
          setNotes={setNotes}
          slipRows={slipRows}
          addSlipFiles={addSlipFiles}
          removeSlipRow={removeSlipRow}
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setAddOpen(false)}
            disabled={uploadingSlips || createPayment.isPending}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            onClick={() => void submitCreate()}
            disabled={uploadingSlips || createPayment.isPending}
          >
            {uploadingSlips || createPayment.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ກຳລັງບັນທຶກ...
              </>
            ) : (
              "ບັນທຶກ"
            )}
          </Button>
        </div>
      </Modal>
    </>
  );
}

function PaymentFormFields({
  isRefund,
  setIsRefund,
  previewKind,
  amount,
  setAmount,
  amountLimitHint,
  paidAt,
  setPaidAt,
  method,
  setMethod,
  notes,
  setNotes,
  slipRows,
  addSlipFiles,
  removeSlipRow,
}: {
  isRefund: boolean;
  setIsRefund: (v: boolean) => void;
  previewKind: QuotationInvoicePaymentKind | null;
  amount: string;
  setAmount: (s: string) => void;
  amountLimitHint: string | null;
  paidAt: string;
  setPaidAt: (s: string) => void;
  method: string;
  setMethod: (s: string) => void;
  notes: string;
  setNotes: (s: string) => void;
  slipRows: PaymentSlipRow[];
  addSlipFiles: (files: FileList | null) => void;
  removeSlipRow: (idx: number) => void;
}) {
  const slipInputId = useId();
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 rounded-md border p-3">
        <Checkbox
          id="pay-refund"
          checked={isRefund}
          onCheckedChange={(v) => setIsRefund(Boolean(v))}
        />
        <label htmlFor="pay-refund" className="cursor-pointer text-sm">
          ຄືນເງິນ (refund)
        </label>
      </div>
      {isRefund ? (
        <p className="text-muted-foreground text-xs">
          ປະເພດທີ່ຈະບັນທຶກ: {paymentKindLabel("refund")}
        </p>
      ) : (
        previewKind && (
          <p className="text-muted-foreground text-xs">
            ປະເພດທີ່ຈະບັນທຶກ: {paymentKindLabel(previewKind)}
          </p>
        )
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="pay-amount">ຈຳນວນເງິນ</Label>
          <Input
            id="pay-amount"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-1 h-9"
          />
          {amountLimitHint && (
            <p className="mt-1 text-muted-foreground text-xs">
              {amountLimitHint}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="pay-date">ວັນທີຊຳລະ</Label>
          <Input
            id="pay-date"
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            className="mt-1 h-9"
          />
        </div>
        <fieldset className="space-y-2 sm:col-span-2">
          <legend className="font-medium text-sm leading-none">ວິທີຊຳລະ</legend>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {PAYMENT_METHOD_OPTIONS.map((opt) => {
              const Icon = opt.Icon;
              const id = `pay-method-${opt.value}`;
              const selected = method === opt.value;
              return (
                <label
                  key={opt.value}
                  htmlFor={id}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-md border p-3 text-left text-sm transition-colors",
                    selected
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-border hover:bg-muted/50",
                  )}
                >
                  <input
                    id={id}
                    name="payment-method"
                    type="radio"
                    value={opt.value}
                    checked={selected}
                    onChange={() => setMethod(opt.value)}
                    className="sr-only"
                  />
                  <Icon
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                  <span className="font-medium">{opt.label}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
      </div>
      <div>
        <Label htmlFor="pay-notes">ຫມາຍເຫດ</Label>
        <Textarea
          id="pay-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="mt-1 min-h-[72px]"
          rows={2}
        />
      </div>
      <div>
        <Label htmlFor={slipInputId}>ສະລິບໂອນ (ຖ້າມີ)</Label>
        <input
          id={slipInputId}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            addSlipFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            onClick={() => document.getElementById(slipInputId)?.click()}
          >
            <Upload className="h-4 w-4" />
            ເລືອກໄຟລ໌
          </Button>
        </div>
        <p className="mt-1 text-muted-foreground text-xs">
          ອັບໂຫຼດໄປຄັງເມື່ອກົດບັນທຶກ — ສາມາດເລືອກຫຼາຍໄຟລ໌
        </p>
        {slipRows.length > 0 && (
          <ul className="mt-2 space-y-1 text-sm">
            {slipRows.map((row, i) => (
              <li
                key={
                  row.source === "server"
                    ? `s-${row.fileKey}-${i}`
                    : `l-${row.id}`
                }
                className="flex items-center justify-between gap-2"
              >
                <span className="truncate text-muted-foreground">
                  {row.source === "server" ? row.fileName : row.file.name}
                  {row.source === "local" && (
                    <span className="ml-2 text-xs opacity-80">(ລໍຖ້າອັບໂຫຼດ)</span>
                  )}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeSlipRow(i)}
                >
                  ລຶບ
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
